<template>
  <el-dialog
    title="关联房屋列表"
    :visible.sync="visible"
    width="800px"
    :before-close="handleClose"
    :disabled="!ruleInfo.ruleId"
  >
    <!-- 规则信息概览：新增费用类型显示 -->
    <div class="rule-info">
      <span class="rule-label">当前规则：</span>
      <span class="rule-value">{{ ruleName }}</span>
      <span class="rule-label">费用类型：</span>
      <span class="rule-value">{{ feeTypeLabel }}</span>
      <span class="rule-label">单价：</span>
      <span class="rule-value">¥{{ pricePerSqm.toFixed(2) }}</span>
      <span class="rule-label">关联房屋：</span>
      <span class="rule-value">{{ totalCount }}</span>
      <span class="rule-label">总价值：</span>
      <span class="rule-value">¥{{ totalValue.toFixed(2) }}</span>
    </div>

    <!-- 无 ruleId 时显示提示 -->
    <div v-if="!ruleInfo.ruleId" class="no-rule-id-tip">
      <i class="el-icon-warning"></i>
      <span>未获取到有效规则ID，无法加载关联房屋数据</span>
    </div>

    <!-- 有 ruleId 时显示搜索框、表格和分页 -->
    <div v-else>
      <el-input
        v-model="searchHouse"
        placeholder="搜索房屋编号或地址..."
        prefix-icon="el-icon-search"
        clearable
        class="mt-20"
        @input="handleSearch"
        @clear="handleSearch"
      />

      <el-table
        :data="filteredHouseList"
        border
        style="width: 100%; margin-top: 20px"
        :empty-text="loading ? '加载中...' : '暂无关联房屋'"
        v-loading="loading"
        element-loading-text="正在加载房屋信息..."
        element-loading-background="rgba(255, 255, 255, 0.8)"
      >
        <el-table-column
          prop="houseName"
          label="房屋编号"
          align="center"
          width="120"
        />
        <el-table-column label="房间号" align="center" width="180">
          <template slot-scope="scope">
            {{ scope.row.floorNum || "-" }}栋-{{
              scope.row.unitNum || "-"
            }}层-{{ scope.row.houseName || "-" }}号
          </template>
        </el-table-column>
        <el-table-column label="面积(㎡)" align="center" width="150">
          <template slot-scope="scope">
            {{ Number(scope.row.builtUpArea || 0).toFixed(2) }}
          </template>
        </el-table-column>
        <!-- 采暖类型显示层高 -->
        <el-table-column
          v-if="isHeatingType"
          label="层高(m)"
          align="center"
          width="120"
        >
          <template slot-scope="scope">
            {{ Number(scope.row.floorHeight || 0).toFixed(2) }}
          </template>
        </el-table-column>
        <el-table-column label="房屋类型" align="center" width="120">
          <template slot-scope="scope">
            {{ getRoomSubTypeLabel(scope.row.roomSubType) }}
          </template>
        </el-table-column>
        <el-table-column label="计算价值" align="center" width="150">
          <template slot-scope="scope">
            ¥{{ calcHouseValue(scope.row).toFixed(2) }}
          </template>
        </el-table-column>
        <el-table-column
          prop="relateDate"
          label="关联日期"
          align="center"
          width="150"
        >
          <template slot-scope="scope">
            {{ scope.row.relateDate ? formatDate(scope.row.relateDate) : "-" }}
          </template>
        </el-table-column>
        <el-table-column label="操作" align="center" width="120">
          <template slot-scope="scope">
            <el-button
              size="mini"
              type="text"
              class="text-danger"
              @click="handleUnRelate(scope.row)"
              :disabled="loading"
            >
              解除关联
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件：直接使用接口返回的 total，总条数>0时显示 -->
      <el-pagination
        v-if="totalCount > 0"
        class="mt-20"
        :current-page="pagination.current"
        :page-sizes="[10, 20, 30, 50]"
        :page-size="pagination.size"
        :total="totalCount"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        background
      />
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button
        type="primary"
        @click="handleRelateNewHouse"
        :disabled="!ruleInfo.ruleId || !ruleInfo.pricePerSqm"
      >
        + 关联新房屋
      </el-button>
    </div>
    <relate-new-house-dialog
      ref="relateNewHouseDialog"
      :rule-id="ruleInfo.ruleId"
      :price-per-sqm="ruleInfo.pricePerSqm"
      :fee-type="ruleInfo.feeType"
      @relateSuccess="handleRelateSuccess"
      :append-to-body="true"
    />
  </el-dialog>
</template>

<script>
import RelateNewHouseDialog from "@/components/calcRule/relateNewHouseDialog";
import {
  getHouseListByRuleId,
  unRelateHouseFromRule,
} from "@/api/community/calcRuleApi";

export default {
  components: { RelateNewHouseDialog },
  name: "RelatePropertyDialog",
  props: {
    ruleInfo: {
      type: Object,
      required: true,
      default: () => ({
        ruleId: "",
        ruleName: "未命名规则",
        pricePerSqm: 0,
        totalValue: 0,
        feeType: "", // 费用类型：rent/heating
      }),
      validator: (val) => {
        // 基础校验
        if (!val || typeof val !== "object") return false;
        return true;
      },
    },
  },
  data() {
    return {
      visible: false,
      searchHouse: "",
      filteredHouseList: [], // 最终显示的过滤+分页数据
      originalHouseList: [], // 接口返回的当前页原始数据
      loading: false,
      totalCount: 0, // 关联房屋总条数（直接用接口返回的 total）
      pagination: {
        current: 1,
        size: 10,
      },
      roomSubTypeOptions: [
        { value: "110", label: "住宅" },
        { value: "119", label: "办公室" },
        { value: "120", label: "宿舍" },
        { value: "10102", label: "其他" },
      ],
      // 费用类型映射
      feeTypeOptions: [
        { value: "rent", label: "房租" },
        { value: "heating", label: "采暖" },
      ],
    };
  },
  computed: {
    ruleName() {
      return this.ruleInfo.ruleName || "未命名规则";
    },
    pricePerSqm() {
      const price = Number(this.ruleInfo.pricePerSqm);
      return isNaN(price) ? 0 : price;
    },
    // 费用类型标签
    feeTypeLabel() {
      if (!this.ruleInfo.feeType) return "未设置";
      const item = this.feeTypeOptions.find(
        (item) => item.value === this.ruleInfo.feeType,
      );
      return item ? item.label : "未知类型";
    },
    // 是否为采暖类型
    isHeatingType() {
      return this.ruleInfo.feeType === "heating";
    },
    // 总价值：根据费用类型计算
    totalValue() {
      return this.originalHouseList.reduce((sum, item) => {
        return sum + this.calcHouseValue(item);
      }, 0);
    },
  },
  watch: {
    "ruleInfo.ruleId"(newVal) {
      if (newVal && this.visible) {
        this.pagination.current = 1;
        this.fetchHouseList();
      }
    },
    "ruleInfo.feeType"() {
      // 费用类型变化时重新计算价值并刷新列表显示
      if (this.visible) {
        this.handleSearch();
      }
    },
    "ruleInfo.pricePerSqm"() {
      // 单价变化时重新计算价值
      if (this.visible) {
        this.handleSearch();
      }
    },
    visible(val) {
      if (val) {
        this.initDialog();
      } else {
        this.resetDialog();
        this.$emit("close");
      }
    },
  },
  methods: {
    /**
     * 初始化弹窗
     */
    initDialog() {
      if (!this.ruleInfo.ruleId) {
        this.$message.warning("规则ID为空，无法加载房屋数据");
        return;
      }
      // 重置分页和搜索
      this.pagination.current = 1;
      this.searchHouse = "";
      // 加载房屋列表
      this.fetchHouseList();
    },

    /**
     * 重置弹窗状态
     */
    resetDialog() {
      this.searchHouse = "";
      this.filteredHouseList = [];
      this.originalHouseList = [];
      this.totalCount = 0;
      this.pagination.current = 1;
      this.pagination.size = 10;
    },

    /**
     * 打开弹窗
     */
    open() {
      if (!this.ruleInfo.ruleId) {
        this.$message.error("请先选择有效的计算规则");
        return;
      }
      // 校验采暖类型是否有单价
      if (this.isHeatingType && this.pricePerSqm <= 0) {
        this.$message.warning("采暖规则未设置有效单价，计算结果可能不准确");
      }
      this.visible = true;
    },

    /**
     * 关闭弹窗
     */
    handleClose() {
      this.visible = false;
    },

    /**
     * 获取关联房屋列表（补全参数，添加feeType）
     */
    async fetchHouseList() {
      if (!this.ruleInfo.ruleId) {
        this.$message.error("规则ID不存在，无法获取房屋列表");
        return;
      }

      this.loading = true;
      try {
        const offset = (this.pagination.current - 1) * this.pagination.size;
        // 补全请求参数：添加feeType
        const res = await getHouseListByRuleId({
          ruleId: this.ruleInfo.ruleId,
          offset: offset,
          row: this.pagination.size,
          keyword: this.searchHouse.trim(),
          feeType: this.ruleInfo.feeType, // 传递费用类型给接口
        });

        if (res.code === 0) {
          this.originalHouseList = res.data || [];
          this.totalCount = res.total || 0;
          this.handleSearch(); // 触发搜索过滤
        } else {
          this.$message.error(res.msg || "获取房屋列表失败");
          this.originalHouseList = [];
          this.filteredHouseList = [];
          this.totalCount = 0;
        }
      } catch (error) {
        console.error("获取房屋列表异常：", error);
        this.$message.error("网络错误，无法获取房屋列表");
        this.originalHouseList = [];
        this.filteredHouseList = [];
        this.totalCount = 0;
      } finally {
        this.loading = false;
      }
    },

    /**
     * 核心：根据费用类型计算房屋价值
     */
    calcHouseValue(row) {
      const price = this.pricePerSqm;
      const area = Number(row.builtUpArea || 0);

      // 房租：单价 × 面积
      if (this.ruleInfo.feeType === "rent") {
        return price * area;
      }
      // 采暖：单价 × 面积 × 层高
      else if (this.ruleInfo.feeType === "heating") {
        const floorHeight = Number(row.floorHeight || 0);
        // 层高为空时提示（可选）
        if (floorHeight <= 0 && row.houseName) {
          console.warn(`房屋${row.houseName}层高为空，采暖计算结果为0`);
        }
        return price * area * floorHeight;
      }
      // 默认逻辑（兼容未设置费用类型）
      else {
        return price * area;
      }
    },

    /**
     * 解除关联房屋
     */
    async handleUnRelate(row) {
      if (!row.roomId || !this.ruleInfo.ruleId) {
        this.$message.error("参数错误，无法解除关联");
        return;
      }

      this.$confirm("确定解除该房屋与规则的关联吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
        closeOnClickModal: false,
      })
        .then(async () => {
          this.loading = true;
          try {
            // 补全解除关联参数：添加feeType
            const res = await unRelateHouseFromRule({
              ruleId: this.ruleInfo.ruleId,
              roomId: row.roomId,
              feeType: this.ruleInfo.feeType,
            });
            if (res.code === 0) {
              this.$message.success("解除关联成功");
              this.fetchHouseList(); // 重新加载列表和总条数
            } else {
              this.$message.error(res.msg || "解除关联失败");
            }
          } catch (error) {
            console.error("解除关联异常：", error);
            this.$message.error("网络错误，解除关联失败");
          } finally {
            this.loading = false;
          }
        })
        .catch(() => {
          this.$message.info("已取消操作");
        });
    },

    /**
     * 打开关联新房屋弹窗
     */
    handleRelateNewHouse() {
      if (!this.ruleInfo.pricePerSqm) {
        this.$message.error("当前规则未设置每平米价格，无法关联房屋");
        return;
      }
      // 采暖类型校验提示
      if (this.isHeatingType) {
        this.$message.info("采暖类型关联房屋时，需确保房屋有层高数据");
      }
      // 打开子弹窗并传递完整参数
      this.$refs.relateNewHouseDialog.open({
        ruleId: this.ruleInfo.ruleId,
        pricePerSqm: this.ruleInfo.pricePerSqm,
        feeType: this.ruleInfo.feeType,
      });
    },

    /**
     * 关联新房屋成功回调
     */
    handleRelateSuccess() {
      this.$message.success("关联房屋成功");
      this.pagination.current = 1;
      this.fetchHouseList(); // 重新加载列表和总条数
    },

    /**
     * 搜索过滤房屋列表（完善空值和容错处理）
     */
    handleSearch() {
      const keyword = this.searchHouse.trim().toLowerCase();
      if (!keyword) {
        this.filteredHouseList = [...this.originalHouseList];
        return;
      }

      // 搜索过滤当前页数据（完善空值处理）
      this.filteredHouseList = this.originalHouseList.filter((item) => {
        const houseName = (item.houseName || "").toLowerCase();
        const communityId = (item.communityId || "").toLowerCase();
        const roomId = (item.roomId || "").toLowerCase();
        const address = (item.address || "").toLowerCase();

        return (
          houseName.includes(keyword) ||
          communityId.includes(keyword) ||
          roomId.includes(keyword) ||
          address.includes(keyword)
        );
      });
    },

    /**
     * 分页-每页条数改变
     */
    handleSizeChange(val) {
      this.pagination.size = val;
      this.pagination.current = 1;
      this.fetchHouseList();
    },

    /**
     * 分页-当前页改变
     */
    handleCurrentChange(val) {
      this.pagination.current = val;
      this.fetchHouseList();
    },

    /**
     * 获取房屋类型标签
     */
    getRoomSubTypeLabel(value) {
      if (!value) return "未知类型";
      const item = this.roomSubTypeOptions.find((item) => item.value === value);
      return item ? item.label : "未知类型";
    },

    /**
     * 日期格式化（如果项目中没有全局工具函数，可自定义）
     */
    formatDate(dateStr) {
      if (!dateStr) return "-";
      try {
        const date = new Date(dateStr);
        return `${date.getFullYear()}-${(date.getMonth() + 1)
          .toString()
          .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
      } catch (e) {
        return dateStr;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.no-rule-id-tip {
  text-align: center;
  padding: 50px 0;
  color: #999;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;

  i {
    font-size: 24px;
    margin-right: 10px;
    color: #e6a23c;
  }
}

.rule-info {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e4e7ed;

  .rule-label {
    color: #666;
    font-size: 14px;
  }
  .rule-value {
    font-weight: 500;
    color: #333;
    font-size: 14px;
  }
}

.mt-20 {
  margin-top: 20px;
  text-align: right;
}

.text-danger {
  color: #f56c6c;
  &:hover {
    color: #f78989;
  }
}

.el-table__empty-text {
  color: #606266;
  font-size: 14px;
  padding: 60px 0;
}

.dialog-footer {
  text-align: right;
  padding: 10px 20px;
}

// 优化分页样式
::v-deep .el-pagination {
  .el-pager li {
    margin: 0 2px;
  }
  .el-pagination__sizes {
    margin-right: 10px;
  }
}

// 优化表格加载样式
::v-deep .el-loading-mask {
  .el-loading-spinner {
    top: 50%;
    margin-top: -25px;
  }
}
</style>
